// src/services/replies.js
//
// 🟢 REPLIES — the quote model, shared by the composer strip, the in-bubble quote and
//    the chat list. Byte-compatible with the phone (ChatModal ~18478-18862).
//
// THE CONTRACT, exactly:
//   message.replyTo = {                      // a Firestore MAP at TOP LEVEL
//     messageId, senderId, text, type,       // `messageId` — NOT `id`
//     systemType, callData, imageUrl, videoUrl,
//     thumbnailBase64?, mediaCount?, hasVideo?, encrypted?
//   }
//   message.replyToPerDeviceKeys?   // seals replyTo.text
//   message.replyThumbPerDeviceKeys?// seals replyTo.thumbnailBase64
//   message.senderDeviceId          // the key BOTH of the above unseal against
//
// 🟥 THE QUOTE IS ENCRYPTED, and a stale comment in the app's own ReplyQuote.tsx says
//    otherwise. When sealing succeeds the phone REDACTS the copy it writes: text
//    becomes the single character '🔒', thumbnailBase64 is deleted outright, and
//    `encrypted: true` is set. Ship a plaintext quote and the words of a private
//    message sit in Firestore in the clear — so this does the same redaction, and
//    only falls back to plaintext when there are no device keys to seal to (which is
//    also what the phone does, and what its readers tolerate).
//
// 🟥 `senderName` IS NEVER STORED. The phone's type declaration lists it and its
//    builder never sets it, so every reader derives the name locally from senderId.
//    Writing one would be dead weight the app ignores — and it's why the phone's
//    WebHistorySync export (`senderName: m.replyTo.senderName`) is always undefined.

import { sealManyForUsers, decryptPerDeviceText } from '../crypto/e2ee';
import { capQuote } from './sealDevices';   // 💬 the most of a quote that is sealed

export const LOCK = '\u{1F512}';

// ReplyQuote.tsx:37-54 — the app's own per-kind palette, icon and corner label.
export const REPLY_ACCENT = {
  text: '', image: '#5C6BC0', sticker: '#EC407A', video: '#EF5350', images: '#5C6BC0',
  audio: '#FF9800', document: '#2196F3', location: '#4CAF50', contact: '#9C27B0',
  poll: '#FFC107', flight: '#00BCD4', shared_post: '#7E57C2', link: '#29B6F6', call: '#4CAF50',
  /* 💬 a reply to somebody's About status — see services/about */
  about: '#8E8E93',
};
export const REPLY_LABEL = {
  image: 'Photo', sticker: 'Sticker', video: 'Video', images: 'Album', audio: 'Voice note',
  document: 'Document', location: 'Location', contact: 'Contact', poll: 'Poll',
  flight: 'Flight', shared_post: 'Post', link: 'Link', call: 'Call', about: 'Status',
};

/** Which visual family a quote belongs to. Mirrors ReplyQuote.tsx:195-217. */
export function replyKind(replyTo, original) {
  const t = replyTo?.type;
  if (replyTo?.systemType === 'call_history' || t === 'missed_call' || replyTo?.callData) return 'call';
  if (t === 'sticker' || original?.isSticker) return 'sticker';
  if (t === 'gif') return 'image';
  if (t === 'about') return 'about';
  if (['image', 'video', 'images', 'audio', 'document', 'location',
    'contact', 'poll', 'flight', 'shared_post'].includes(t)) return t;
  return 'text';
}

/**
 * The snapshot text stored on the quote — the phone's exact strings, because this
 * string is what the OTHER client renders in its chat list and in any quote whose
 * original has scrolled out of memory. Diverge here and the two clients disagree
 * about what was quoted.
 *
 * Ordering matters and is copied deliberately: a captioned photo quotes its CAPTION,
 * not "📷 Photo" (`replyingTo.text ||` wins in ChatModal:18482).
 */
export function quoteTextFor(m) {
  if (!m) return 'Message';
  const cap = (m._caption || m.caption || '').trim();
  switch (m.type) {
    case 'text': return (m._plain || m.text || '').trim() || 'Message';
    /* 🟥 THE STATUS ITSELF IS THE QUOTE. Without this case an About reply fell to
       the default and quoted the word "Message" — the reply would arrive on the
       phone quoting nothing the person ever said. */
    case 'about': return (m.text || '').trim() || 'Status';
    case 'image': return cap || '\u{1F4F7} Photo';
    case 'sticker': return cap || '\u{1F49F} Sticker';
    case 'gif': return cap || '\u{1F39E}️ GIF';
    case 'video': return cap || '\u{1F3A5} Video';
    case 'images': return cap || '\u{1F4F7} Photos';
    case 'audio': return '\u{1F3A4} Voice message';
    case 'document': return `\u{1F4C4} Document: ${m.documentName || 'File'}`;
    case 'location': return '\u{1F4CD} Location';
    case 'contact': return `\u{1F464} Contact: ${m._payload?.name || m.contactName || 'Unknown User'}`;
    case 'poll': return `\u{1F4CA} Poll: ${m._payload?.question || m.question || 'Poll'}`;
    case 'flight': return `✈️ Flight: ${m.flightData?.flightNumber || m.flightNumber || 'Info'}`;
    default:
      if (m.systemType === 'call_history' || m.type === 'missed_call') {
        return `${m.callData?.callType === 'video' ? '\u{1F4F9} Video' : '\u{1F4DE} Voice'} Call`;
      }
      return (m._plain || m.text || '').trim() || 'Message';
  }
}

/** The 80px JPEG the phone puts on the quote. We already HAVE one on every media
 *  doc — imageThumbnailBase64 / videoThumbnailBase64 / batchThumbnails[0] — so
 *  there is nothing to generate and nothing to re-encode. */
function thumbFor(m) {
  if (!m) return null;
  if (m.type === 'image' || m.type === 'sticker' || m.type === 'gif') return m.imageThumbnailBase64 || null;
  if (m.type === 'video') return m.videoThumbnailBase64 || null;
  if (m.type === 'images') return (Array.isArray(m.batchThumbnails) && m.batchThumbnails.find(Boolean)) || null;
  return null;
}

/** The plaintext quote, before sealing. */
export function quoteFrom(m) {
  if (!m?.id) return null;
  const thumb = thumbFor(m);
  const isAlbum = m.type === 'images';
  return {
    messageId: m.id,
    senderId: m.senderId || null,
    text: quoteTextFor(m),
    type: m.type || 'text',
    systemType: m.systemType || null,
    callData: m.callData || null,
    imageUrl: m.imageUrl || (Array.isArray(m.images) ? m.images[0] : null) || null,
    videoUrl: m.videoUrl || null,
    ...(thumb ? { thumbnailBase64: thumb } : {}),
    // 🧩 one item of an album was chosen — which one (services/mediaRuns albumItemReplySource)
    ...(Number.isInteger(m._replyAlbumIndex) ? { itemIndex: m._replyAlbumIndex } : {}),
    ...(isAlbum && Array.isArray(m.images) && m.images.length ? {
      mediaCount: m.images.length,
      hasVideo: Array.isArray(m._mediaBatch) ? m._mediaBatch.some((x) => x?.kind === 'video') : false,
    } : {}),
  };
}

/** 🟥 Firestore REJECTS an undefined field value outright — `setDoc` throws rather than
 *  skipping the key. One `undefined` anywhere in this object loses the whole message,
 *  so the quote is scrubbed before it can reach a write. */
function noUndefined(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => { if (obj[k] !== undefined) out[k] = obj[k]; });
  return out;
}

/**
 * Seal a quote and return the fields to spread into BOTH the Firestore doc and the
 * socket `extra`. Never throws — a failed seal degrades to a plaintext quote rather
 * than losing the reply.
 *
 * @returns {Promise<{}|{replyTo, replyToPerDeviceKeys?, replyThumbPerDeviceKeys?, senderDeviceId}>}
 */
export async function buildReplyFields(srcMessage, participants) {
  const raw = quoteFrom(srcMessage);
  if (!raw) return {};
  const plain = noUndefined(raw);

  /* 💬 only the quote's start is sealed — it is sealed once per device, and the bubble shows
        the original from memory when it has it (`quoteCut`; ./sealDevices.capQuote) */
  const _q = capQuote(plain.text || '');
  const quotedText = _q.text;
  // 🟢 the thumbnail is sealed ONCE PER DEVICE, so its ciphertext is multiplied by the
  //    number of paired devices in the chat. A phone-sized 80px JPEG is ~1-3 KB, but a
  //    quote that inherited a bigger one from elsewhere times 20 devices would push the
  //    message doc toward Firestore's 1 MB ceiling — and a rejected write loses the
  //    whole message, not just the picture. Past that point the quote goes without one.
  const quotedThumb = (plain.thumbnailBase64 && plain.thumbnailBase64.length <= 6000)
    ? plain.thumbnailBase64 : null;
  if (plain.thumbnailBase64 && !quotedThumb) delete plain.thumbnailBase64;

  try {
    // 🟢 ONE device-directory read for BOTH seals. The phone calls sealReply… and
    //    sealReplyThumb… separately, which re-reads every participant's device list a
    //    second time; on a large group that's a visible pause before the message goes.
    const sealed = await sealManyForUsers([quotedText, quotedThumb], participants);
    const textMap = sealed?.maps?.[0];
    const thumbMap = sealed?.maps?.[1];
    const hasText = textMap && Object.keys(textMap).length > 0;
    const hasThumb = thumbMap && Object.keys(thumbMap).length > 0;

    if (hasText || hasThumb) {
      const wire = { ...plain, encrypted: true };
      if (hasText) wire.text = LOCK;
      if (hasText && _q.cut) wire.quoteCut = true;
      // 🟥 REDACT BY INTENT, NOT BY OUTCOME. Gating this on `hasThumb` meant that if the
      //    text sealed but the thumbnail's seal came back empty, we'd ship encrypted:true
      //    with a PLAINTEXT thumbnail attached — a picture of the quoted message sitting
      //    in Firestore beside a padlocked line of text. Once we've decided to encrypt,
      //    an unsealed thumbnail is dropped rather than sent in the clear.
      if (quotedThumb) delete wire.thumbnailBase64;
      return {
        replyTo: wire,
        ...(hasText ? { replyToPerDeviceKeys: textMap } : {}),
        ...(hasThumb ? { replyThumbPerDeviceKeys: thumbMap } : {}),
        senderDeviceId: sealed.senderDeviceId,
      };
    }
  } catch (e) {
    console.warn('[reply] seal failed — sending the quote in the clear:', e?.message);
  }
  // no device keys anywhere (pre-pairing peer) — the app accepts a plaintext quote
  return { replyTo: plain };
}

// ── reading ────────────────────────────────────────────────────────────────
//
// An unseal is a nacl.box.open against an already-cached device key, but the message
// list re-paints on every incoming message and every media resolve, so without this
// the same quote would be reopened dozens of times per chat. Keyed by message id
// because a quote is immutable once written.
const _hydrated = new Map();

/** The readable quote for a message: unsealed when possible, snapshot otherwise. */
export async function hydrateReplyTo(msg) {
  const rt = msg?.replyTo;
  if (!rt) return null;
  if (!rt.encrypted) return rt;
  if (msg.id && _hydrated.has(msg.id)) return _hydrated.get(msg.id);

  const out = { ...rt };
  let opened = false;
  // 🟥 the seals open against the REPLYING message's senderDeviceId/senderId — not
  //    the quoted message's. Using replyTo.senderId here silently yields nothing.
  const shim = (perDeviceKeys) => ({
    perDeviceKeys, senderDeviceId: msg.senderDeviceId, senderId: msg.senderId,
  });
  try {
    if (msg.replyToPerDeviceKeys) {
      const t = await decryptPerDeviceText(shim(msg.replyToPerDeviceKeys));
      if (t != null) { out.text = t; opened = true; }
    }
    if (msg.replyThumbPerDeviceKeys) {
      const b = await decryptPerDeviceText(shim(msg.replyThumbPerDeviceKeys));
      if (b) { out.thumbnailBase64 = b; opened = true; }
    }
  } catch { /* the redacted snapshot still renders as a typed quote */ }
  // 🟥 ONLY CACHE A SUCCESS. decryptPerDeviceText never throws — it returns null, and
  //    getDeviceKeys swallows a failed read and caches the empty directory for 60s. So a
  //    single offline moment produced a quote that stayed padlocked *for the life of the
  //    page*, immune to reopening the chat or reconnecting. Caching only what actually
  //    opened means the next repaint retries instead.
  if (opened) {
    out.encrypted = false;
    if (msg.id) _hydrated.set(msg.id, out);
    return out;
  }
  return out;
}

/** True when the quote is still redacted — the reader shows a type label instead. */
export const isRedacted = (t) => !t || t === LOCK || String(t).startsWith(LOCK);

/**
 * What the chat list says a reply was about. The phone shows the QUOTED message,
 * not the reply's own words — "You replied to: “a poll”" — and falls back through
 * type labels whenever the snapshot is still sealed. Truncated at 30 like the app.
 */
export function replySnippet(replyTo) {
  const r = replyTo || {};
  if (!isRedacted(r.text)) return trim30(r.text);
  const byType = {
    image: '\u{1F4F7} Photo', sticker: '\u{1F49F} Sticker', gif: '\u{1F39E}️ GIF',
    video: '\u{1F3A5} Video', images: '\u{1F4F7} Multiple Photos',
    audio: '\u{1F3A4} Voice message', document: '\u{1F4C4} Document',
    location: '\u{1F4CD} Location', contact: '\u{1F464} Contact',
    poll: 'a poll', flight: 'a flight',
  };
  return byType[r.type] || 'a message';
}
const trim30 = (s) => (String(s).length > 30 ? `${String(s).slice(0, 30)}…` : String(s));
